import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { useDemo } from "@/store/DemoStore";
import { Avatar, Empty, SearchInput } from "@/components/common";
import { ChatThread } from "@/components/ChatThread";
import { cn } from "@/lib/utils";
import { fmtRelative } from "@/lib/trip";

function ConvList({ list, activeId, client, trip }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const shown = list.filter((c) => client(c.clientId).name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-4 border-b border-slate-200 p-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy-900" data-testid="page-title">Messages</h1>
        <SearchInput value={q} onChange={setQ} placeholder="Search conversations" testId="messages-search-input" />
      </div>
      <ul className="scroll-area min-h-0 flex-1 p-2" data-testid="conversation-list">
        {shown.map((c) => {
          const last = c.messages[c.messages.length - 1];
          const t = trip(c.tripId);
          return (
            <li key={c.id}>
              <button onClick={() => nav(`/agent/messages/${c.id}`)} data-testid={`conversation-item-${c.id}`} className={cn("flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors", activeId === c.id ? "bg-navy-50" : "hover:bg-slate-50")}>
                <Avatar name={client(c.clientId).name} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className={cn("truncate text-sm text-navy-900", c.unreadAgent ? "font-bold" : "font-semibold")}>{client(c.clientId).name}</p>
                    <span className="shrink-0 text-[11px] text-slate-400">{last && fmtRelative(last.at)}</span>
                  </div>
                  {t && <p className="truncate text-[11px] font-medium text-slate-400">{t.title}</p>}
                  <div className="flex items-center gap-2">
                    <p className={cn("min-w-0 flex-1 truncate text-xs", c.unreadAgent ? "text-navy-900" : "text-slate-500")}>{last ? `${last.from === "agent" ? "You: " : ""}${last.text}` : "No messages yet"}</p>
                    {c.unreadAgent > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-sky-500 px-1.5 text-[10px] font-bold text-white">{c.unreadAgent}</span>}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Thread({ conv, clientId }) {
  const { client, trip, send, markRead } = useDemo();
  const c = client(clientId);
  const t = conv ? trip(conv.tripId) : null;
  const len = conv?.messages.length;
  useEffect(() => { if (conv?.unreadAgent) markRead(conv.id, "agent"); }, [conv?.id, len]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="conversation-workspace">
      <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-3 py-3 sm:px-5">
        <Link to="/agent/messages" data-testid="messages-back-btn" aria-label="Back" className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100 lg:hidden"><ArrowLeft className="h-5 w-5" /></Link>
        <Avatar name={c.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-navy-900" data-testid="conversation-client-name">{c.name}</p>
          {t ? <Link to={`/agent/trips/${t.id}`} className="block truncate text-xs text-slate-500 hover:text-navy-900" data-testid="conversation-trip-link">{t.title} · {t.bookingNumber || t.quoteNumber}</Link> : <p className="text-xs text-slate-500">{c.email}</p>}
        </div>
        <Link to={`/agent/clients/${c.id}`} data-testid="conversation-profile-link" className="hidden rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-navy-900 hover:bg-slate-50 sm:block">View Profile</Link>
      </div>
      <ChatThread className="min-h-0 flex-1" messages={conv?.messages || []} viewer="agent" readAt={conv?.clientReadAt} onSend={(text) => send(c.id, "agent", text, conv?.tripId)} placeholder={`Reply to ${c.name.split(" ")[0]}…`} />
    </div>
  );
}

export default function MessagesPage() {
  const { id } = useParams();
  const { conversations, client, trip } = useDemo();
  const list = [...conversations].sort((a, b) => (b.messages.at(-1)?.at || "").localeCompare(a.messages.at(-1)?.at || ""));
  const conv = conversations.find((c) => c.id === id);
  const clientId = conv?.clientId || (id && client(id.replace("conv-", "")) ? id.replace("conv-", "") : null);

  return (
    <div className="flex h-full min-h-0 bg-white" data-testid="messages-page">
      <div className={cn("h-full min-h-0 w-full shrink-0 border-r border-slate-200 lg:block lg:w-[340px]", clientId && "hidden")}>
        <ConvList list={list} activeId={id} client={client} trip={trip} />
      </div>
      <div className={cn("h-full min-h-0 min-w-0 flex-1", !clientId && "hidden lg:block")}>
        {clientId ? <Thread key={clientId} conv={conv} clientId={clientId} /> : <div className="grid h-full place-items-center bg-slate-50/60"><Empty icon={MessageSquare} title="Select a conversation" text="Choose a client on the left to view the thread." /></div>}
      </div>
    </div>
  );
}
